import React, { useEffect, useRef } from 'react'

function FeaturedProjectModal({ project, onClose }) {
  const closeRef = useRef(null)

  useEffect(() => {
    if (!project) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    // focus close button for accessibility
    if (closeRef.current && typeof closeRef.current.focus === 'function') closeRef.current.focus()
    return () => window.removeEventListener('keydown', onKey)
  }, [project, onClose])

  if (!project) return null

  const duration = project.getDuration ? project.getDuration() : null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Project details: ${project.title}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)',
        zIndex: 2000,
        padding: 20,
        animation: 'fadeIn .12s ease-in'
      }}
    >
      <div style={{ maxWidth: 900, width: '100%', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', gap: 0, flexDirection: 'column' }}>
          {/* header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottom: '1px solid #eee' }}>
            <div>
              <h3 style={{ margin: 0 }}>{project.title}</h3>
              <div style={{ color: '#666', fontSize: 13, marginTop: 6 }}>{project.role}{duration ? ` • ${duration}` : ''}</div>
            </div>
            <div>
              <button ref={closeRef} aria-label="Close project dialog" onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
          </div>

          {/* body */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
            <div style={{ padding: 20, borderRight: '1px solid #f4f4f6', minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {project.imageUrl ? (
                <div style={{ width: '100%', height: 220, background: `url(${project.imageUrl}) center/cover no-repeat` }} />
              ) : (
                <div style={{ width: '80%', height: 160, background: '#eef2ff', borderRadius: 8 }} />
              )}
            </div>

            <div style={{ padding: 20 }}>
              <p style={{ color: '#333', lineHeight: 1.6 }}>{project.description}</p>

              {project.technologies && project.technologies.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <strong style={{ fontSize: 13 }}>Tech</strong>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {project.technologies.map(t => <span key={t} style={{ background: '#f5f7ff', padding: '6px 8px', borderRadius: 6, fontSize: 12 }}>{t}</span>)}
                  </div>
                </div>
              )}

              {project.features && project.features.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <strong style={{ fontSize: 13 }}>Features</strong>
                  <ul style={{ marginTop: 8, color: '#444' }}>
                    {project.features.map(f => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              )}

              <div style={{ marginTop: 18, display: 'flex', gap: 12 }}>
                {project.liveUrl && <a href={project.liveUrl} target="_blank" rel="noreferrer" style={{ color: '#667eea', textDecoration: 'none', fontWeight: 700 }}>View live</a>}
                {project.repoUrl && <a href={project.repoUrl} target="_blank" rel="noreferrer" style={{ color: '#667eea', textDecoration: 'none', fontWeight: 700 }}>View source</a>}
              </div>
            </div>
          </div>

          {/* footer */}
          {project.startDate || project.endDate ? (
            <div style={{ padding: 16, borderTop: '1px solid #eee', color: '#666', fontSize: 13 }}>
              <div>Duration: {project.getDuration ? project.getDuration() : '—'}</div>
              <div>Start: {project.startDate || '—'} • End: {project.endDate || '—'}</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default FeaturedProjectModal
